import React, { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { extractYouTubeVideoId } from "@/lib/partners";
import { Youtube } from "lucide-react";

/**
 * YouTube trailer URL input + preview thumbnail (MOVIE posts, admin form only).
 * Emits `onChange(url)` and displays `<img>` preview when a valid 11-char id is
 * extracted.
 */
export const YouTubeTrailerSection = ({ value = "", onChange, testidPrefix = "trailer" }) => {
    const videoId = useMemo(() => extractYouTubeVideoId(value), [value]);
    const invalid = value && !videoId;
    return (
        <div className="rounded-2xl border border-neutral-200 bg-white p-4" data-testid={`${testidPrefix}-section`}>
            <div className="mb-2 flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-rose-100 text-rose-600">
                    <Youtube size={14} />
                </div>
                <div className="text-sm font-bold text-neutral-800">YouTube trailer (optional)</div>
            </div>
            <Label className="text-xs">Trailer URL</Label>
            <Input
                placeholder="https://www.youtube.com/watch?v=…  or  https://youtu.be/…"
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                maxLength={500}
                data-testid={`${testidPrefix}-url`}
                className={invalid ? "border-rose-400" : ""}
            />
            {invalid && (
                <p className="mt-1 text-[11px] font-medium text-rose-600" data-testid={`${testidPrefix}-error`}>
                    Not a recognized YouTube URL. Try a watch, youtu.be or shorts link.
                </p>
            )}
            {videoId && (
                <div className="mt-3">
                    <img
                        src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                        alt="Trailer thumbnail"
                        className="max-h-40 rounded-xl border border-neutral-200"
                        data-testid={`${testidPrefix}-thumbnail`}
                    />
                    <div className="mt-1 text-[10px] font-mono text-neutral-500" data-testid={`${testidPrefix}-videoid`}>
                        video_id: {videoId}
                    </div>
                </div>
            )}
        </div>
    );
};

export default YouTubeTrailerSection;
